import { FC, useEffect, useState } from "react";
import ButtonComponent from "../../../../../common/components/button/button.component";
import DriverDetailsCandidatureManagementComponent from "./management/driver-details-candidature-management.component";
import userTranslationsHelper from "../../../../../languages/user-translations.helper";
import notificationService from "../../../../../common/utils/notification/notification.service";
import driverDetailsUserPermissionsHelper from "../common/user-permissions/driver-details-user-permission.helper";
import { useAppContext } from "../../../../../context/app.context";
import DriverDetailsDriverPlanEntry from "../active-route-plans/common/types/driver-details-plan-entry";
import ContentWrapper from "../../../../../common/components/content-wrapper/content-wrapper.component";
import DriverCandidatureActiveError, {
  DriverCandidatureActiveErrorType,
} from "../../../../../common/services/driver/candidature/active/driver-candidature-active-error";
import driverService from "../../../../../common/services/driver/driver.service";
import DriverCandidatureActiveParams from "../../../../../common/services/driver/candidature/active/driver-candidature-active-params";
import useAbort from "../../../../../common/hooks/use-abort";

type DriverDetailsCandidatureProps = {
  driverUuid: string;
  isVisible: boolean;
  onAcceptCandidatureSuccess: () => void;
  currentPlanEntries: DriverDetailsDriverPlanEntry[];
  asCardComponent?: boolean;
};

const DriverDetailsCandidatureComponent: FC<DriverDetailsCandidatureProps> = (
  props,
) => {
  const translations =
    userTranslationsHelper.getDriverDetailsCandidatureTranslations();

  const { user } = useAppContext();

  const userPermissions = driverDetailsUserPermissionsHelper.getPermissions(
    user?.roles!,
  );

  const [isActiveCandidature, setIsActiveCandidature] = useState(false);

  const activeCandidatureAbort = useAbort();

  const [
    isCandidatureManagementModalOpen,
    setIsCandidatureManagementModalOpen,
  ] = useState(false);

  const onFetchCandidatureSuccess = () => {
    setIsActiveCandidature(true);
  };

  const onFetchCandidatureFailure = () => {
    notificationService.error(
      translations.failureCandidatureFetchNotificationText,
    );
  };

  const handleCandidatureError = (error: DriverCandidatureActiveError) => {
    if (error.type === DriverCandidatureActiveErrorType.NOT_ACTIVE) {
      setIsActiveCandidature(false);
    } else {
      onFetchCandidatureFailure();
    }
  };

  const fetchCandidature = () => {
    if (!props.driverUuid) {
      return;
    }

    const params: DriverCandidatureActiveParams = {
      driverUuid: props.driverUuid,
    };

    driverService
      .getActiveCandidature(params, activeCandidatureAbort.signal)
      .then(onFetchCandidatureSuccess)
      .catch(handleCandidatureError);
  };

  useEffect(() => {
    if (!props.isVisible || !userPermissions.hasAccessToManageCandidature) {
      return;
    }

    fetchCandidature();

    return activeCandidatureAbort.revoke;
  }, [props.isVisible]);

  const openCandidatureManagementModal = () => {
    setIsCandidatureManagementModalOpen(true);
  };

  const closeCandidatureManagementModal = () => {
    setIsCandidatureManagementModalOpen(false);
  };

  const onManageCandidatureButtonClick = () => {
    openCandidatureManagementModal();
  };

  const onAcceptCandidatureSuccess = () => {
    fetchCandidature();
    closeCandidatureManagementModal();
    props.onAcceptCandidatureSuccess();
  };

  const onDeleteCandidatureSuccess = () => {
    fetchCandidature();
    closeCandidatureManagementModal();
  };

  if (!isActiveCandidature) {
    return null;
  }

  return (
    <ContentWrapper
      asCardComponent={props.asCardComponent}
      classNames={{ root: "mb-2" }}
    >
      <div className="mb-5">
        <div className="mb-1">
          {translations.activeCandidatureNotificationText}
        </div>
        <ButtonComponent
          onClick={onManageCandidatureButtonClick}
          type="primary"
          title={translations.manageCandidatureButtonTitle}
        >
          {translations.manageCandidatureButtonText}
        </ButtonComponent>
        <DriverDetailsCandidatureManagementComponent
          isOpen={isCandidatureManagementModalOpen}
          onClose={closeCandidatureManagementModal}
          driverUuid={props.driverUuid}
          onAcceptCandidatureSuccess={onAcceptCandidatureSuccess}
          onDeleteCandidatureSuccess={onDeleteCandidatureSuccess}
          currentPlanEntries={props.currentPlanEntries}
        />
      </div>
      <DriverDetailsCandidatureManagementComponent
        isOpen={isCandidatureManagementModalOpen}
        onClose={closeCandidatureManagementModal}
        driverUuid={props.driverUuid}
        onAcceptCandidatureSuccess={onAcceptCandidatureSuccess}
        onDeleteCandidatureSuccess={onDeleteCandidatureSuccess}
        currentPlanEntries={props.currentPlanEntries}
      />
    </ContentWrapper>
  );
};

export default DriverDetailsCandidatureComponent;
